import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UsersThree, Plus, EnvelopeSimple, Trash, CheckCircle } from "@phosphor-icons/react";

export default function Trusted() {
    const { t } = useApp();
    const [list, setList] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/trusted");
            setList(r.data);
        } catch {}
    };
    useEffect(() => { load(); }, []);

    const handleAdd = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const r = await api.post("/trusted", { name, email: email.toLowerCase() });
            setList((prev) => [...prev, r.data]);
            setName(""); setEmail(""); setShowAdd(false);
            toast.success(t("trusted.invited"));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Fehler");
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/trusted/${id}`);
            setList((prev) => prev.filter((x) => x.trusted_id !== id));
            toast.success(t("trusted.deleted"));
        } catch { toast.error("Fehler"); }
    };

    return (
        <AppShell>
            <div className="mx-auto max-w-4xl px-6 lg:px-10 py-10 lg:py-14">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">
                            <UsersThree size={14} weight="fill" aria-hidden="true" /> Vertrauenspersonen
                        </div>
                        <h1 className="mt-2 font-heading font-bold text-shield-ink text-3xl sm:text-4xl" data-testid="trusted-title">
                            {t("trusted.title")}
                        </h1>
                        <p className="mt-2 text-shield-muted max-w-xl">{t("trusted.sub")}</p>
                    </div>
                    <button
                        onClick={() => setShowAdd((s) => !s)}
                        className="inline-flex items-center gap-2 h-12 px-5 pill bg-shield-forest text-white font-semibold hover:bg-shield-forestDeep transition-colors"
                        data-testid="trusted-add-btn"
                    >
                        <Plus size={16} weight="bold" aria-hidden="true" /> {t("trusted.addTitle")}
                    </button>
                </div>

                {showAdd && (
                    <form onSubmit={handleAdd} className="mt-8 card-soft p-6 lg:p-8 space-y-4" data-testid="trusted-add-form">
                        <input
                            type="text" required value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full h-14 rounded-xl border-2 border-shield-line px-4 text-lg focus:border-shield-forest focus:outline-none bg-white"
                            placeholder={t("trusted.name")}
                            data-testid="trusted-name-input"
                        />
                        <input
                            type="email" required value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full h-14 rounded-xl border-2 border-shield-line px-4 text-lg focus:border-shield-forest focus:outline-none bg-white"
                            placeholder={t("trusted.email")}
                            data-testid="trusted-email-input"
                        />
                        <button
                            type="submit" disabled={saving}
                            className="inline-flex items-center gap-2 h-12 px-6 pill bg-shield-terracotta text-white font-semibold hover:bg-shield-terracottaDeep transition-colors disabled:opacity-70"
                            data-testid="trusted-save-btn"
                        >
                            <EnvelopeSimple size={16} weight="bold" aria-hidden="true" />
                            {saving ? t("common.loading") : t("trusted.add")}
                        </button>
                    </form>
                )}

                <div className="mt-10 space-y-4">
                    {list.length === 0 ? (
                        <div className="card-soft p-8 text-center text-shield-muted">{t("trusted.empty")}</div>
                    ) : list.map((p) => (
                        <div key={p.trusted_id} className="card-soft p-6 flex items-center justify-between gap-4" data-testid={`trusted-row-${p.trusted_id}`}>
                            <div className="flex items-center gap-4">
                                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-shield-forest/10 text-shield-forest font-bold text-lg">
                                    {(p.name || "?").slice(0, 1).toUpperCase()}
                                </div>
                                <div>
                                    <div className="font-heading font-semibold text-shield-ink">{p.name}</div>
                                    <div className="text-sm text-shield-muted">{p.email}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-shield-success bg-shield-success/10 h-8 px-3 pill">
                                    <CheckCircle size={12} weight="fill" aria-hidden="true" /> eingeladen
                                </span>
                                <button
                                    onClick={() => handleDelete(p.trusted_id)}
                                    className="inline-flex items-center gap-1.5 h-10 px-3 pill text-shield-error hover:bg-shield-error/10 font-semibold transition-colors"
                                    data-testid={`trusted-delete-${p.trusted_id}`}
                                >
                                    <Trash size={14} weight="bold" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </AppShell>
    );
}
